import React, { useEffect, useState } from "react";

import { useTheme } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import { Box, Chip, Typography, Tooltip } from "@mui/material";

import { isEmpty } from "lodash";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  EditorState,
  convertToRaw,
  convertFromHTML,
  ContentState,
  Modifier,
} from "draft-js";
import draftjsToHtml from "draftjs-to-html";

import RGBColorChart from "./RGBColorChart.json";
import { purposeTags } from "global/constants";
import { useMaterialUIController } from "context";

const TAG_DESCRIPTIONS = {
  brandName:
    "Indicates the company or brand name so users instantly recognize who the message is from.",
  placeAddress:
    "Shows the complete address of the location to help users identify where they need to go.",
  lotName:
    "Represents the name of the specific parking lot where the customer is parking.",
  displayName:
    "Displays the selected parking rate name, making it easy for users to identify their choice.",
  parentDisplayName:
    "Shows the name of the parent or main rate when the selected rate belongs to a group.",
  rateTitle:
    "Provides the title or name of the parking rate to help users understand the rate type.",
  amount: "Indicates the price of the selected parking rate.",
  baseRate: "Shows the original parking rate before applying fees or taxes.",
  serviceFee:
    "Represents any additional service or processing fee applied to the booking.",
  tax: "Displays the total tax added to the parking cost.",
  cityTax: "Shows the tax amount specific to the city.",
  countyTax: "Shows the tax amount charged at the county level.",
  totalAmount:
    "Indicates the final payable amount including all fees and taxes.",
  licensePlate:
    "Displays the vehicle’s license plate number provided by the customer.",
  validationLink:
    "Provides a link that customers can use to validate their parking.",
  startDate: "Shows the date when the parking session begins.",
  startTime: "Displays the exact time the parking session starts.",
  endDate: "Shows the date when the parking session ends.",
  endTime: "Displays the time when the parking session ends.",
  receiptURL:
    "Provides a link where customers can view or download their parking receipt.",
  extensionLink:
    "Shows a link that allows the customer to extend their parking session.",
  updateLicensePlateLink:
    "Shows a link that allows the customer to update their current license plate.",
    currentLicensePlate:
    "Current license plate",
    oldLicensePlate:"Old License Plate"
};

const RichTextEditor = (props) => {
  const { label, name, value, setFieldValue, error, helperText, purpose } =
    props;

  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const theme = useTheme();
  const { palette } = theme;
  const { grey, error: colorError, success: colorSuccess } = palette;

  const blocksFromHTML = convertFromHTML(value || "");
  const state = ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap,
  );

  const [editorState, setEditorState] = useState(() =>
    isEmpty(value)
      ? EditorState.createEmpty()
      : EditorState.createWithContent(state),
  );

  const [hasFocus, setHasFocus] = useState(false);
  const [mentionTags, setMentionTags] = useState([]);

  const onEditorStateChange = async (eData) => {
    setEditorState(eData);

    const data = draftjsToHtml(convertToRaw(eData.getCurrentContent()));
    setFieldValue(name, data);

    if (!eData.getCurrentContent().hasText()) {
      setFieldValue(name, "");
    }
  };

  const getBorder = () => {
    if (error) return `1px solid ${colorError}`;
    if (hasFocus) return `2px solid blue`;
    return `1px solid ${grey[200]}`;
  };

  useEffect(() => {
    const tags =
      purposeTags[purpose ?? ""] ||
      (purpose?.endsWith("(On Rate Selection)")
        ? purposeTags["On Rate Selection"]
        : null) ||
      (purpose?.endsWith("(Extension Welcome)")
        ? purposeTags["Extension Welcome"]
        : null) ||
      [];
    setMentionTags(tags);
  }, [purpose]);

  const insertTagAtCursor = (tag) => {
    let contentState = editorState.getCurrentContent();
    let selectionState = editorState.getSelection();

    const startOffset = selectionState.getStartOffset();
    const blockKey = selectionState.getStartKey();
    const currentBlock = contentState.getBlockForKey(blockKey);
    const blockText = currentBlock.getText();

    if (startOffset > 0 && blockText[startOffset - 1] === "{") {
      const newSelection = selectionState.merge({
        anchorOffset: startOffset - 1,
        focusOffset: startOffset,
      });

      contentState = Modifier.removeRange(
        contentState,
        newSelection,
        "backward",
      );
    }

    const newContentState = Modifier.insertText(
      contentState,
      contentState.getSelectionAfter(),
      `{{${tag}}}`,
    );

    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      "insert-characters",
    );

    setEditorState(newEditorState);

    const html = draftjsToHtml(
      convertToRaw(newEditorState.getCurrentContent()),
    );

    setFieldValue(name, html);
  };

  return (
    <>
      <FormControl error={error} fullWidth variant="outlined">
        <InputLabel
          htmlFor="content-field"
          sx={{
            color: hasFocus ? colorSuccess.main : "inherit",
            top: "-20px",
            left: "-2px",
            padding: "1px 4px",
            fontSize: "0.875rem",
            backgroundColor: ({ palette }) => palette.background.paper,
          }}
        >
          {label}
        </InputLabel>

        <Editor
          toolbarHidden={false}
          name={name}
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
          wrapperStyle={{ border: getBorder(), borderRadius: "6px" }}
          editorStyle={{
            minHeight: "160px",
            padding: "8px 12px",
            background: "transparent",
          }}
          toolbarStyle={{ background: "transparent" }}
          toolbar={{
            options: [
              "inline",
              "blockType",
              "fontSize",
              "list",
              "textAlign",
              "colorPicker",
              "link",
              "emoji",
              "remove",
              "history",
            ],
            colorPicker: { colors: RGBColorChart },
          }}
          mention={{
            separator: " ",
            trigger: "{",
            suggestions: mentionTags?.map((tag) => ({
              text: tag,
              value: `{${tag}}}`,
            })),
          }}
        />

        <Box mt={1} sx={{ borderTop: "1px solid #eee", paddingTop: "8px" }}>
          {mentionTags.length > 0 ? (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {mentionTags.map((tag) => (
                <Tooltip
                  key={tag}
                  title={TAG_DESCRIPTIONS[tag] || `Use {{${tag}}}`}
                  arrow
                  placement="top"
                >
                  <Chip
                    label={`{{${tag}}}`}
                    size="small"
                    clickable
                    color={sidenavColor}
                    variant="outlined"
                    onClick={() => insertTagAtCursor(tag)}
                    sx={{ fontSize: "12px", cursor: "pointer" }}
                  />
                </Tooltip>
              ))}
            </Box>
          ) : (
            <Typography variant="caption" sx={{ pl: 0.5 }}>
              This purpose does not have any dynamic parameters.
            </Typography>
          )}
        </Box>

        <FormHelperText error>{helperText}</FormHelperText>
      </FormControl>
    </>
  );
};

export default RichTextEditor;
